import React, { Fragment, useState, useRef, useEffect } from 'react';

import cls from 'classnames';

import _noop from 'lodash/noop';

import CameraIcon from '@ott/icon-camera';
import LoadingDots from '@ott/loading-dots';

import styles from './ImageLoader.scss';

const hasIntersectionObserver =
  typeof window !== 'undefined' && typeof window.IntersectionObserver !== 'undefined';

interface EventPayload {
  /**
   * @deprecated use `event` instead
   */
  e?: Event | string;
  event?: Event | string;
  src?: string;
  id?: unknown;
}

interface Props {
  id?: unknown;
  src: string;
  onLoad?: (payload: EventPayload) => void;
  onError?: (payload: EventPayload) => void;
  /**
   * @deprecated use `isLazy` instead
   */
  lazyLoad?: boolean;
  isCropped?: boolean;
  isLazy?: boolean;
  className?: string;
  style?: React.CSSProperties;
  widthWhileLoading?: string | number;
  childrenClassNames?: {
    imageClass?: string;
  };
  intersectionRoot?: Element | null;
  intersectionThreshold?: number;
}

const getInitialState = (src: string, isLazy: boolean) => ({
  isVisible: !isLazy || !hasIntersectionObserver,
  isLoaded: false,
  isFailed: !src, // для мгновенного фейла при отсутствии src
});

const ImageLoader: React.FC<Props> = (props) => {
  const {
    id,
    src,
    onLoad = _noop,
    onError = _noop,
    lazyLoad = false,
    isCropped = false,
    className,
    intersectionRoot = null,
    intersectionThreshold = 0,
    widthWhileLoading = 'auto',
    style: styleByProps,
    childrenClassNames: { imageClass } = {},
  }: Props = props;

  /**
   * Сделано для поддержки старого пропа
   */
  let { isLazy = false } = props;
  isLazy ||= lazyLoad;

  const [state, setState] = useState(() => getInitialState(src, isLazy));
  const { isLoaded, isFailed, isVisible } = state;

  const containerRef = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>();

  const handleLoad = (event: Event) => {
    setState((prev) => ({
      ...prev,
      isLoaded: true,
      isFailed: false,
    }));

    onLoad({
      event,
      e: event,
      src, // legacy support
      id, // legacy support
    });
  };

  const handleError = (event?: Event | string) => {
    setState((prev) => ({
      ...prev,
      isFailed: true,
    }));

    onError({ e: event, event });
  };

  const destroyImage = () => {
    if (!image.current) {
      return;
    }

    image.current.onload = _noop;
    image.current.onerror = _noop;
  };

  const createImage = () => {
    /**
     * создаем изображение заранее, чтобы навесить
     * onload/onerror, при рендере картинка возьмется из memory cache
     */

    image.current = new Image();
    image.current.onload = handleLoad;
    image.current.onerror = handleError;
  };

  const loadImage = (source: string) => {
    if (!source || !image.current) {
      handleError();

      return;
    }

    image.current.src = source;
  };

  useEffect(() => {
    setState(getInitialState(src, isLazy));

    if (src) {
      createImage();
    } else {
      handleError();
    }

    return destroyImage;
  }, [src, isLazy]);

  useEffect(() => {
    if (!isLazy || !hasIntersectionObserver) {
      loadImage(src);

      return _noop;
    }

    const observerCallback = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver
    ) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return;
        }

        loadImage(src);
        if (observer) {
          observer.unobserve(target);
        }
        setState((prev) => ({ ...prev, isVisible: true }));
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: intersectionRoot || null,
      threshold: intersectionThreshold,
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (observer && typeof observer.disconnect === 'function') {
        observer.disconnect();
        // в IE 11 не поддерживается
      }
    };
  }, [isLazy, src, intersectionRoot]);

  const style = { ...styleByProps };

  if (!isCropped && !isLoaded) {
    // ширина временного контейнера без картинки
    style.width = widthWhileLoading;
  }

  const errorNode = isFailed && <CameraIcon fill="#dadada" />;
  const loaderNode = isLoaded || isFailed ? null : <LoadingDots />;

  const renderImage = () => {
    if (isFailed || !isLoaded) {
      return null;
    }

    if (!isCropped) {
      return <img className={cls(styles.image, imageClass)} src={src} alt="" />;
    }

    return (
      <div
        className={cls(styles.cropper, imageClass)}
        style={{
          backgroundImage: `url('${src}')`,
        }}
      />
    );
  };

  return (
    <div
      className={cls(styles.container, className, isCropped && styles.isCropped)}
      // @ts-ignore
      style={style}
      ref={containerRef}
    >
      {isVisible || !isLazy ? (
        <>
          {renderImage()}
          {loaderNode}
          {errorNode}
        </>
      ) : null}
    </div>
  );
};

export default ImageLoader;
